import React from 'react';
import { StrapiIcon } from 'components/StrapiIcon';
import { getFeatureFallbackIcon } from 'constants/featureIconFallbacks';
import { cn } from 'lib/utils';

interface IconWithFallbackProps {
  /** URL of the Strapi icon (highest priority) */
  strapiIconUrl?: string | null;
  /** Feature name for fallback icon lookup */
  featureName?: string | null;
  /** Path for fallback when no feature (first-level menus) */
  fallbackPath?: string | null;
  /** Alt text for accessibility */
  alt?: string;
  /** Additional CSS classes */
  className?: string;
  /** Icon size (width and height) in pixels */
  size?: number;
}

/**
 * IconWithFallback - Smart icon component with 4-level fallback hierarchy
 *
 * Fallback priority:
 * 1. Strapi icon (if URL provided) - renders StrapiIcon
 * 2. Feature-specific Lucide icon (if feature name matches) - from FEATURE_ICON_FALLBACKS
 * 3. Path-based Lucide icon (if path matches) - from PATH_ICON_FALLBACKS
 * 4. Default Package icon - when no match found
 *
 * @example
 * ```tsx
 * // With Strapi icon (Level 1)
 * <IconWithFallback
 *   strapiIconUrl="https://strapi.example.com/icon.svg"
 *   featureName="forms"
 *   alt="Forms icon"
 * />
 *
 * // With feature fallback (Level 2)
 * <IconWithFallback
 *   strapiIconUrl={null}
 *   featureName="lead-request-management"
 * />
 *
 * // With path fallback (Level 3)
 * <IconWithFallback
 *   strapiIconUrl={null}
 *   featureName={null}
 *   fallbackPath="/events"
 * />
 *
 * // With default fallback (Level 4)
 * <IconWithFallback
 *   strapiIconUrl={null}
 *   featureName={null}
 * />
 * ```
 */
export const IconWithFallback: React.FC<IconWithFallbackProps> = ({
  strapiIconUrl,
  featureName,
  fallbackPath,
  alt = '',
  className,
  size = 24,
}) => {
  // Level 1: Try Strapi icon first
  if (strapiIconUrl) {
    return <StrapiIcon url={strapiIconUrl} alt={alt} className={className} size={size} />;
  }

  // Level 2: Feature-specific icon
  // Level 3: Path-based icon (for first-level menus)
  // Level 4: Default Package icon
  const FallbackIcon = getFeatureFallbackIcon(featureName, fallbackPath);

  return (
    <FallbackIcon
      className={cn('flex-shrink-0', className)}
      width={size}
      height={size}
      aria-label={alt || 'Menu icon'}
    />
  );
};

export default IconWithFallback;
